import React, { useMemo, useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { employeesAPI } from "../services/employeesService";
import { admissionsAPI } from "../services/admissionService";
import { feesAPI } from "../services/feesService";

/* ================= MOCK DATA (SIMULATING FINANCE DATA) ================= */

const salaryPayments = Array.from({ length: 30 }, (_, i) => {
  const day = (i % 28) + 1;
  return {
    id: i + 1,
    paid_amount: Math.floor(Math.random() * 20000) + 20000,
    paid_at: `2025-12-${String(day).padStart(2, "0")}`,
  };
});

/* ================= COMPONENT ================= */

const Analysis = () => {
  const [filterType, setFilterType] = useState("day");
  const [selectedDate, setSelectedDate] = useState(new Date("2026-01-01"));
  const [selectedEmployee, setSelectedEmployee] = useState("all");

  const [admissioners, setAdmissioners] = useState([]);
  const [admissions, setAdmissions] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState({ cash: 0, upi: 0, card: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchPaymentSummary();
  }, [filterType, selectedDate]);

  const formattedDate = useMemo(() => {
    if (!selectedDate) return "";
    const y = selectedDate.getFullYear();
    const m = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const d = String(selectedDate.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [selectedDate]);

  const fetchPaymentSummary = async () => {
    if (!selectedDate) return;

    try {
      const params = { filterType };

      if (filterType === 'day') {
        params.date = formattedDate;
      } else if (filterType === 'month') {
        params.month = selectedDate.getMonth() + 1;
        params.year = selectedDate.getFullYear();
      } else if (filterType === 'year') {
        params.year = selectedDate.getFullYear();
      }

      const res = await feesAPI.getFeePayments(params);
      if (res.data.success) {
        setPaymentSummary(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching payment summary:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [admissionersRes, admissionsRes] = await Promise.all([
        employeesAPI.getAdmissioners(),
        admissionsAPI.getAdmissions()
      ]);

      if (admissionersRes.data.success) {
        setAdmissioners(admissionersRes.data.data || []);
      }

      if (admissionsRes.data.data) {
        setAdmissions(admissionsRes.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching analysis data:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ================= SALARY SUMMARY ================= */

  const filteredSalaries = useMemo(() => {
    const dateStr = formattedDate;
    return salaryPayments.filter(s =>
      filterType === "day"
        ? s.paid_at === dateStr
        : filterType === "month"
          ? s.paid_at.startsWith(dateStr.slice(0, 7))
          : s.paid_at.startsWith(dateStr.slice(0, 4))
    );
  }, [filterType, formattedDate]);

  /* ================= ADMISSION FILTER ================= */

  const filteredAdmissions = useMemo(() => {
    let filtered = admissions;
    if (selectedEmployee !== "all") {
      filtered = filtered.filter(a => a.admissionerId === Number(selectedEmployee));
    }
    return filtered;
  }, [selectedEmployee, admissions]);

  /* ================= UI TEMPLATES ================= */

  const amountBodyTemplate = (rowData) => {
    return `₹${rowData.paid_amount.toLocaleString()}`;
  };

  const studentNameTemplate = (rowData) => {
    return `${rowData.firstName} ${rowData.lastName}`;
  };

const dateTemplate = (rowData) => {
  if (!rowData.admissionDate) return "-";

  const date = new Date(rowData.admissionDate);
  date.setMinutes(date.getMinutes() + date.getTimezoneOffset());

  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' }); // Jan, Feb
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};


  const filterOptions = [
    { label: "Day", value: "day" },
    { label: "Month", value: "month" },
    { label: "Year", value: "year" },
  ];

  const employeeOptions = [
    { label: "All Employees", value: "all" },
    ...admissioners.map(emp => ({
      label: `${emp.firstName} ${emp.lastName}`,
      value: emp.id
    }))
  ];

  const tableHeaderStyle = {
    backgroundColor: '#0056D2',
    color: 'white',
    fontWeight: 'bold'
  };

  return (
    <div className="p-6 space-y-8 bg-gray-50/50 min-h-screen">
      {/* HEADER & FILTER CARD */}
      <div className="bg-white/80 backdrop-blur-xl rounded-xl shadow-lg border border-white/20 p-4 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-indigo-500/10 rounded-full -translate-y-16 translate-x-16"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-400/10 to-pink-400/10 rounded-full translate-y-12 -translate-x-12"></div>

        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-lg">
            <i className="pi pi-chart-bar text-white text-sm"></i>
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Analysis Overview</h3>
            <p className="text-xs text-gray-600 mt-0.5">Financial and Admission Data Analysis</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 relative z-10">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-700 flex items-center">
              <i className="pi pi-calendar-plus mr-1 text-blue-500 text-xs"></i>Filter Type
            </label>
            <Dropdown
              value={filterType}
              options={filterOptions}
              onChange={(e) => setFilterType(e.value)}
              className="w-full text-sm border border-gray-200 rounded-lg"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-700 flex items-center">
              <i className="pi pi-calendar mr-1 text-indigo-500 text-xs"></i>Select Date
            </label>
            <Calendar
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.value)}
              view={filterType === "month" ? "month" : filterType === "year" ? "year" : "date"}
              dateFormat={filterType === "month" ? "mm/yy" : filterType === "year" ? "yy" : "dd/mm/yy"}
              className="w-full text-sm"
              inputClassName="py-2 border border-gray-200 rounded-lg"
              showIcon
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-700 flex items-center">
              <i className="pi pi-user mr-1 text-purple-500 text-xs"></i>Admissioner
            </label>
            <Dropdown
              value={selectedEmployee}
              options={employeeOptions}
              onChange={(e) => setSelectedEmployee(e.value)}
              className="w-full text-sm border border-gray-200 rounded-lg"
              filter
              placeholder="Select Admissioner"
            />
          </div>
        </div>
      </div>

      {/* PAYMENT SUMMARY CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {Object.entries(paymentSummary).map(([mode, amount], index) => {
          const colors = [
            "from-emerald-500 to-teal-600",
            "from-blue-500 to-indigo-600",
            "from-orange-500 to-amber-600"
          ];
          const icons = ["pi-money-bill", "pi-mobile", "pi-credit-card"];

          return (
            <div key={mode} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex items-center gap-4 hover:shadow-md transition-shadow">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors[index]} flex items-center justify-center shadow-lg`}>
                <i className={`pi ${icons[index]} text-white text-xl`}></i>
              </div>
              <div>
                <p className="text-gray-500 text-xs font-medium uppercase tracking-wider">{mode}</p>
                <p className="text-2xl font-bold text-gray-900">₹{amount.toLocaleString()}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* SALARY PAYMENTS TABLE */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <i className="pi pi-wallet text-blue-500"></i>
              Salary Payments
            </h2>
          </div>
          <DataTable
            value={filteredSalaries}
            rows={5}
            paginator
            className="p-datatable-sm"
            emptyMessage="No salary records found"
            responsiveLayout="scroll"
          >
            <Column field="paid_at" header="Paid Date"></Column>
            <Column field="paid_amount" header="Amount" body={amountBodyTemplate}></Column>
          </DataTable>
        </div>

        {/* ADMISSIONS TABLE */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <i className="pi pi-user-plus text-indigo-500"></i>
              Admissions
            </h2>
            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-sm">
              {filteredAdmissions.length}
            </span>
          </div>
          <DataTable
            value={filteredAdmissions}
            rows={5}
            paginator
            className="p-datatable-sm"
            emptyMessage="No admissions found"
            responsiveLayout="scroll"
            loading={loading}
          >
            <Column field="id" header="ID"></Column>
            <Column header="Student" body={studentNameTemplate}></Column>
            <Column field="className" header="Class"></Column>
            <Column header="AdmissionDate" body={dateTemplate}></Column>
            <Column field="parentName" header="Parent"></Column>
          </DataTable>
        </div>
      </div>
    </div>
  );
};

export default Analysis;
